import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { NAV_LINKS } from "./navLinks";
import { useScrollLock } from "../../hooks/useScrollLock";

// Fades in on its own once the user has scrolled roughly one viewport height
// past the top, independently of the Hero's internal scroll-progress math.
// Rendered with a very high z-index so it always stays above every section
// that slides on top of the Hero, instead of getting covered like before.
const FADE_START_VH = 1;
const FADE_END_VH = 1.2;

export default function HeroNavbar() {
  const [navP, setNavP] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let rafId = 0;
    let pending = false;

    const compute = () => {
      pending = false;
      const vh = window.innerHeight;
      const start = vh * FADE_START_VH;
      const end = vh * FADE_END_VH;
      const raw = end > start ? (window.scrollY - start) / (end - start) : 0;
      setNavP(Math.min(1, Math.max(0, raw)));
    };

    const onScroll = () => {
      if (pending) return;
      pending = true;
      rafId = requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(rafId);
    };
  }, []);

  useScrollLock(menuOpen);

  // The scroll lock pins scrollY to 0 while the drawer is open, which would
  // otherwise fade this bar out via the scroll listener above. Keep it fully
  // visible whenever the drawer is open, regardless of scroll position.
  const effectiveNavP = menuOpen ? 1 : navP;

  return (
    <nav
      className="fixed left-0 right-0 top-0 flex items-center justify-between"
      style={{
        zIndex: 999,
        padding: "20px 16px",
        opacity: effectiveNavP,
        transform: `translateY(${(1 - effectiveNavP) * -14}px)`,
        willChange: "opacity, transform",
        pointerEvents: effectiveNavP > 0.5 ? "auto" : "none",
        fontFamily: "'Space Grotesk', system-ui, sans-serif",
      }}
    >
      <Link
        to="/"
        className="text-white"
        style={{ fontWeight: 700, fontSize: 17, letterSpacing: -0.2, textDecoration: "none" }}
      >
        Intellivex LTD
      </Link>

      {/* Desktop nav */}
      <div className="hidden md:flex items-center gap-1">
        {NAV_LINKS.slice(0, -1).map((link) => (
          <Link
            key={link.path}
            to={link.path}
            className="whitespace-nowrap cursor-pointer"
            style={{
              background: "transparent",
              color: "#fff",
              borderRadius: 9999,
              padding: "8px 14px",
              fontWeight: 500,
              fontSize: 13,
              border: "none",
              textDecoration: "none",
              transition: "color 0.3s ease",
            }}
          >
            {link.label}
          </Link>
        ))}
        <Link
          to="/contact-us"
          className="whitespace-nowrap cursor-pointer"
          style={{
            background: "#E9E9E9",
            color: "#000",
            borderRadius: 9999,
            padding: "8px 18px",
            fontWeight: 600,
            fontSize: 13,
            border: "none",
            textDecoration: "none",
          }}
        >
          Contact Us
        </Link>
      </div>

      {/* Mobile hamburger */}
      <button
        type="button"
        onClick={() => setMenuOpen(!menuOpen)}
        className="md:hidden flex items-center justify-center cursor-pointer"
        style={{
          width: 40,
          height: 40,
          background: "transparent",
          border: "none",
          color: "#fff",
        }}
        aria-label={menuOpen ? "Close menu" : "Open menu"}
      >
        <div style={{ position: "relative", width: 22, height: 16 }}>
          <span
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 22,
              height: 2,
              borderRadius: 2,
              background: "#fff",
              transform: menuOpen ? "translateY(7px) rotate(45deg)" : "none",
              transition: "transform 0.3s ease",
            }}
          />
          <span
            style={{
              position: "absolute",
              top: 7,
              left: 0,
              width: 22,
              height: 2,
              borderRadius: 2,
              background: "#fff",
              opacity: menuOpen ? 0 : 1,
              transition: "opacity 0.2s ease",
            }}
          />
          <span
            style={{
              position: "absolute",
              top: 14,
              left: 0,
              width: 22,
              height: 2,
              borderRadius: 2,
              background: "#fff",
              transform: menuOpen ? "translateY(-7px) rotate(-45deg)" : "none",
              transition: "transform 0.3s ease",
            }}
          />
        </div>
      </button>

      {/* Mobile drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "100%",
          height: "100vh",
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
          zIndex: 998,
          opacity: menuOpen ? 1 : 0,
          pointerEvents: menuOpen ? "auto" : "none",
          transition: "opacity 0.35s ease",
        }}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "min(320px, 85vw)",
          height: "100vh",
          background: "#0D0D0D",
          zIndex: 999,
          padding: "24px 20px",
          transform: menuOpen ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1)",
        }}
      >
        <div className="flex items-center justify-between mb-10">
          <Link
            to="/"
            onClick={() => setMenuOpen(false)}
            style={{
              fontSize: 18,
              fontWeight: 700,
              color: "#ECECEC",
              textDecoration: "none",
              letterSpacing: -0.2,
            }}
          >
            Intellivex LTD
          </Link>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="flex items-center justify-center cursor-pointer"
            style={{
              width: 36,
              height: 36,
              background: "transparent",
              border: "none",
              color: "#8A8A8A",
              fontSize: 22,
            }}
            aria-label="Close menu"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="flex flex-col" style={{ gap: 4 }}>
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className="whitespace-nowrap cursor-pointer flex items-center"
              style={{
                color: "#ECECEC",
                textDecoration: "none",
                fontSize: 20,
                fontWeight: 500,
                padding: "14px 12px",
                borderRadius: 12,
                transition: "color 0.3s ease, background 0.3s ease",
                letterSpacing: "-0.01em",
              }}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div
          style={{
            position: "absolute",
            bottom: 40,
            left: 20,
            right: 20,
            borderTop: "1px solid rgba(255,255,255,0.08)",
            paddingTop: 20,
          }}
        >
          <Link
            to="/contact-us"
            onClick={() => setMenuOpen(false)}
            className="whitespace-nowrap cursor-pointer flex items-center justify-center"
            style={{
              background: "#2563EB",
              color: "#ECECEC",
              border: "none",
              borderRadius: 9999,
              padding: "13px 0",
              fontWeight: 600,
              fontSize: 15,
              textDecoration: "none",
              width: "100%",
            }}
          >
            Contact Us
          </Link>
        </div>
      </div>
    </nav>
  );
}
